import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { message, Typography } from 'antd';
import PublicHomePage from './components/PublicHomePage';
import './App.css';
import './clinicShell.css'; // 2026-10-27 提升为全局：XmKpis 条带样式原在 ClinicDashboard 懒加载块内，总后台"用户管理"拿不到（坑再证：全局样式必须顶层 import）
import './quickConsult.css'; // 2026-10-31 快速问诊页（全点选）：页面级布局全局引入，不随懒加载 chunk 丢失
import './consultBlocks.css'; // 2026-11-04 复诊对比/辨证依据强调卡片：诊所端与患者侧三页共用，全局引入
import './adminShell.css'; // 2026-11-01 总后台视觉体系（作用域 .xm-admin/.admin-login，含登录页），需在登录页渲染前可用
import './room/theme.css';
import './room/orig.css';
import './room/room.css';
import './room/pages/subpage.css';
import './mobile.css';
import './responsive.css'; // 2026-10-28 全站响应式令牌层：必须排在所有页面 CSS 之后（同一层叠下后者生效）

const { Title, Text } = Typography;

// ===== 懒加载非首页组件（按需加载） =====
const IntroPage = lazy(() => import('./components/IntroPage'));
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const ClinicDashboard = lazy(() => import('./components/ClinicDashboard'));
const RecordSharePage = lazy(() => import('./components/RecordSharePage'));
const CouponSharePage = lazy(() => import('./components/CouponSharePage'));
const CouponRedeemPage = lazy(() => import('./components/CouponRedeemPage'));
const RoomConsult = lazy(() => import('./room/pages/ConsultPage'));
const RoomCheckup = lazy(() => import('./room/pages/CheckupEntry'));
const RoomFamily = lazy(() => import('./room/pages/FamilyForm'));
const RoomFamilyEdit = lazy(() => import('./room/pages/FamilyInfoChange'));
const RoomHealthRecord = lazy(() => import('./room/pages/HealthRecord'));
const RoomCheckupTabs = lazy(() => import('./room/pages/CheckupTabs'));
const RoomReport = lazy(() => import('./room/pages/Report'));
const TrialChatPage = lazy(() => import('./components/TrialChatPage'));

// ===== Loading 组件 ===== */
function PageLoading() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a4fa0 0%, #215fc6 40%, #3b82e3 100%)',
      color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif',
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: 14, background: 'rgba(255,255,255,.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
        marginBottom: 16, animation: 'xpulse 2s ease-in-out infinite',
      }}>⚕️</div>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>加载中...</div>
      <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
        {[0,1,2].map(i => <div key={i} style={{
          width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,.4)',
          animation: `xbounce 1.4s ease-in-out ${i*.2}s infinite`,
        }}/>)}
      </div>
      <style>{`
        @keyframes xpulse{0%,100%{transform:scale(1);opacity:.8}50%{transform:scale(1.05);opacity:1}}
        @keyframes xbounce{0%,80%,100%{transform:translateY(0);background:rgba(255,255,255,.3)}40%{transform:translateY(-8px);background:rgba(255,255,255,.8)}}
      `}</style>
    </div>
  );
}

// ========== 内嵌登录框 ==========
function InlineLogin({ onLogin, title, subtitle, scope = 'clinic' }) {
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async () => {
    if (!username || !password) { message.warning('请输入账号密码'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // 会话按后台分键隔离：admin_* / clinic_* 互不覆盖（修复串页面）
        localStorage.setItem(scope + '_token', data.token || 'ok');
        localStorage.setItem(scope + '_user', username);
        localStorage.setItem(scope + '_role', data.role);
        if (data.tenantId) localStorage.setItem(scope + '_tenant', data.tenantId);
        onLogin(data);
      } else {
        message.error(data.message || '账号或密码错误');
      }
    } catch (e) {
      message.error('登录失败，请检查网络');
    } finally { setLoading(false); }
  };

  // 总后台登录（2026-11-01 参照「基元律动」账户区重做）：暖纸底、墨色主字、金色点缀；
  // 诊所登录页保持原样（下方 return 逐字未动）。
  if (scope === 'admin') {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <div className="admin-login-brand">
            <div className="admin-login-mark">铭</div>
            <div className="admin-login-brandtext">
              <strong>小铭健康</strong>
              <span>ADMIN CONSOLE</span>
            </div>
          </div>
          <h1>超级管理员登录</h1>
          <p className="admin-login-sub">平台运营 · 内容资产 · 系统配置</p>
          <div className="admin-login-field">
            <span>账号</span>
            <input value={username} onChange={e => setUsername(e.target.value)} placeholder="请输入管理员账号"
              autoComplete="username"
              onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }} />
          </div>
          <div className="admin-login-field">
            <span>密码</span>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="请输入登录密码"
              autoComplete="current-password"
              onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }} />
          </div>
          <button className="admin-login-submit" onClick={handleSubmit} disabled={loading}>
            {loading ? '登录中…' : '登 录'}
          </button>
          <a className="admin-login-back" href="/">← 返回首页</a>
        </div>
        <div className="admin-login-foot">小铭健康科技 · 平台总后台</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a4fa0 0%, #215fc6 40%, #3b82e3 100%)',
      padding: 16,
    }}>
      <div className="inline-login-card" style={{
        background: '#fff', borderRadius: 16, padding: '48px 40px', width: '100%', maxWidth: 400,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {/* 2026-10-27 用户令：总后台 emoji 清理（诊所登录页保留原样） */}
          {scope !== 'admin' && <div style={{ fontSize: 40, marginBottom: 12 }}>⚕️</div>}
          <Title level={3} style={{ margin: 0 }}>{title}</Title>
          <Text type="secondary">{subtitle}</Text>
        </div>
        <div style={{ marginBottom: 16 }}>
          <input value={username} onChange={e => setUsername(e.target.value)} placeholder="用户名"
            onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
            style={{ width: '100%', height: 44, border: '1px solid #d9d9d9', borderRadius: 8, padding: '0 12px', fontSize: 15 }} />
        </div>
        <div style={{ marginBottom: 24 }}>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="密码"
            onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
            style={{ width: '100%', height: 44, border: '1px solid #d9d9d9', borderRadius: 8, padding: '0 12px', fontSize: 15 }} />
        </div>
        <button onClick={handleSubmit} disabled={loading}
          style={{ width: '100%', height: 44, background: '#215fc6', color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          {loading ? '登录中...' : '登 录'}
        </button>
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <a href="/" style={{ color: '#999', fontSize: 13 }}>← 返回首页</a>
        </div>
      </div>
    </div>
  );
}

// 2026-10-21 患者登录退役；综合问诊页按原地址恢复，其他 room 页面仍退役。

// ========== 总后台 /admin ==========
function AdminPage() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const role = localStorage.getItem('admin_role');
    if (token && role === 'SUPER_ADMIN') setLoggedIn(true);
    setChecking(false);
  }, []);

  if (checking) return null;

  if (!loggedIn) {
    return (
      <InlineLogin scope="admin" title="总后台管理" subtitle="超级管理员登录"
        onLogin={(data) => {
          if (data.role !== 'SUPER_ADMIN') {
            message.error('此账号无总后台权限，诊所端请访问 /clinic');
            return;
          }
          setLoggedIn(true);
        }} />
    );
  }

  return <Suspense fallback={<PageLoading />}><AdminLayout onLogout={() => { ['admin_token', 'admin_user', 'admin_role', 'admin_tenant'].forEach(k => localStorage.removeItem(k)); setLoggedIn(false); }} /></Suspense>;
}

// ========== 诊所端 /clinic ==========
function ClinicPage() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [checking, setChecking] = useState(true);
  const [username, setUsername] = useState('');
  const [tenantId, setTenantId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // B 端页面：禁止搜索引擎收录（C 端公开页不受影响）
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  // 返回键契约守卫（2026-10-26，登录页起即生效——ClinicDashboard 挂载前的登录页也要守住）：
  // 垫层 20 层 + 面板记录；触达垫层（=到头再按返回/连点/多步手势）做"软刷新"（组件重挂载=刷新效果），
  // 且每次软刷新立刻补满 20 层——单次/连按/快速连按/多步返回都不会落到上一个文档，不弹确认、不离开。
  // 仅长按历史列表直接选最老的"新标签页"这类跨文档跳转由 beforeunload 兜底（浏览器边界，无法静默取消）。
  useEffect(() => {
    const GUARD_LAYERS = 20;
    const pushGuards = () => {
      for (let i = 0; i < GUARD_LAYERS; i++) window.history.pushState({ xg: i }, '', window.location.href);
    };
    try {
      window.history.replaceState({ xg: 0 }, '', window.location.href);
      pushGuards();
      window.history.pushState({ menu: 'dashboard' }, '', window.location.href);
    } catch (err) { /* 历史不可用降级 */ }
    const softRefresh = () => {
      setReloadKey(k => k + 1); // 重挂载门诊工作台/登录卡=刷新效果，不产生文档导航
      try {
        pushGuards(); // 每次返回后补满垫层：下一次/连续返回仍在垫层内，不会落到上个文档
        window.history.pushState({ menu: 'dashboard' }, '', '/clinic');
      } catch (err) { /* 降级 */ }
    };
    const onPop = (e) => {
      if (e.state && e.state.menu) return; // 面板记录：ClinicDashboard 的监听器恢复面板
      softRefresh();                       // 到头=刷新效果（软刷新，无 reload 空窗）
    };
    // 终极兜底：长按历史/多步手势直接选中跨文档条目（如最老的新标签页）时，浏览器强制离开确认；
    // 正常返回路径因 20 层垫层不会走到这里。
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('clinic_token');
    const role = localStorage.getItem('clinic_role');
    if (token && (role === 'TENANT_ADMIN' || role === 'DOCTOR')) {
      setLoggedIn(true);
      setUsername(localStorage.getItem('clinic_user') || '');
      setTenantId(localStorage.getItem('clinic_tenant'));
    }
    setChecking(false);
  }, []);

  if (checking) return null;

  if (!loggedIn) {
    return (
      <InlineLogin key={reloadKey} scope="clinic" title="诊所工作台" subtitle="仅限诊所管理员与医生登录"
        onLogin={(data) => {
          if (data.role === 'SUPER_ADMIN') {
            message.info('超级管理员请访问总后台地址登录');
            return;
          }
          if (data.role !== 'TENANT_ADMIN' && data.role !== 'DOCTOR') {
            message.error('诊所工作台仅限诊所管理员与医生登录');
            return;
          }
          setLoggedIn(true);
          setUsername(data.username);
          setTenantId(data.tenantId);
        }} />
    );
  }

  return <Suspense fallback={<PageLoading />}><ClinicDashboard key={reloadKey} onLogout={() => { ['clinic_token','clinic_user','clinic_role','clinic_tenant'].forEach(k => localStorage.removeItem(k)); setLoggedIn(false); }} username={username} tenantId={tenantId} /></Suspense>;
}

// ========== 站点标题/Logo 同步（跟随后台站点配置，全站生效） ==========
function SiteBrandSync() {
  useEffect(() => {
    fetch('/api/site-settings').then(r => r.json()).then(cfg => {
      if (cfg && cfg.page_title) document.title = cfg.page_title;
      if (cfg && cfg.header_logo) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
        link.href = cfg.header_logo;
      }
    }).catch(() => {});
  }, []);
  return null;
}

// ========== 主入口 ==========
function App() {
  return (
    <BrowserRouter>
      <SiteBrandSync />
      <Routes>
        <Route path="/" element={<PublicHomePage />} />
        <Route path="/intro" element={<Suspense fallback={<PageLoading />}><IntroPage /></Suspense>} />
        <Route path="/admin-x2m6" element={<AdminPage />} />
        <Route path="/admin" element={
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f6f8f9' }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#2c3e50', marginBottom: 10 }}>请联系管理员</div>
            <div style={{ fontSize: 14, color: '#6b7d8e' }}>或拨打客服电话 <span style={{ color: '#215fc6', fontWeight: 600 }}>400-115-9099</span></div>
          </div>
        } />
        <Route path="/clinic" element={<ClinicPage />} />
        {/* 病历扫码分享（2026-10-30）：公开只读页，令牌即凭证（随机 32hex + 有效期），医生可重新生成作废 */}
        <Route path="/record-share/:token" element={<RecordSharePage />} />
        {/* 优惠券领券页（2026-10-31）：公开只读券面，令牌即凭证（同病历分享口径） */}
        <Route path="/coupon-share/:token" element={<CouponSharePage />} />
        {/* 员工核销页（2026-10-31）：员工手机扫患者券面核销码打开；核销动作仍需诊所账号 */}
        <Route path="/coupon-redeem/:token" element={<CouponRedeemPage />} />
        <Route path="/room/consult" element={<Suspense fallback={null}><RoomConsult /></Suspense>} />
        {/* 原中医体检线（2026-10-27 用户指令恢复，页面文件未动）：选体检人→健康档案问诊→四Tab体检→报告 + 家庭成员建档/编辑 */}
        <Route path="/room/checkup" element={<Suspense fallback={null}><RoomCheckup /></Suspense>} />
        <Route path="/room/checkup/family" element={<Suspense fallback={null}><RoomFamily /></Suspense>} />
        <Route path="/room/checkup/family/edit" element={<Suspense fallback={null}><RoomFamilyEdit /></Suspense>} />
        <Route path="/room/checkup/record" element={<Suspense fallback={null}><RoomHealthRecord /></Suspense>} />
        <Route path="/room/checkup/tabs" element={<Suspense fallback={null}><RoomCheckupTabs /></Suspense>} />
        <Route path="/room/checkup/report" element={<Suspense fallback={null}><RoomReport /></Suspense>} />
        {/* 其余退役患者端地址仍重定向到问诊页 */}
        <Route path="/room" element={<Navigate to="/chat" replace />} />
        <Route path="/room/*" element={<Navigate to="/chat" replace />} />
        <Route path="/chat" element={<Suspense fallback={<PageLoading />}><TrialChatPage /></Suspense>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
